import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { PRODUCT_EVENT } from '../store/actionTypes';
import { ProductEvent } from './models/productEvents';

const query = `
  description {
    text
    language
  }
  name {
    displayName
    language
  }
  recurringEventId
  endTime {
    dateTime
    timeZone
  }
  startTime {
    dateTime
    timeZone
  }
  prices {
    currencies {
      amount
      code
      amountInUnit
      id
    }
    description {
      language
      text
    }
    id
    productId
    split
  }
  organizer {
    profile {
      firstname
      lastname
      avatar {
        contentUrl
        medium
      }
      bio
      certifications {
        category
        countryOfOrigin
        periodOfStudyFrom
        periodOfStudyTo
        issuedByName
      }
      experiences {
        from
        to
        organizationId
      }
      preference {
        gradeLevels
      }
    }
    preferences {
      knowsLanguages
    }
  }
`;

export const productEvents = createApi({
  reducerPath: PRODUCT_EVENT,
  baseQuery,
  tagTypes: ['ProductEvent'],
  endpoints: builder => ({
    productEvents: builder.query<
      ProductEvent[],
      {
        page?: number;
        limit?: number;
        earliest?: Date | string;
        latest?: Date | string;
        mingroup?: number;
        maxgroup?: number;
        location?: string;
        recurringId?: string;
      }
    >({
      query: variables => ({
        document: gql`
          query GetProductEvents(
            $page: Int
            $limit: Int
            $earliest: DateTime
            $latest: DateTime
            $mingroup: Int
            $maxgroup: Int
            $location: String
            $recurringId: String
          ) {
            getProductEvents(
              page: $page
              limit: $limit
              earliest: $earliest
              latest: $latest
              mingroup: $mingroup
              maxgroup: $maxgroup
              location: $location
              recurringId: $recurringId
            ) {
              calendarId
              prices {
                currencies {
                  amount
                  code
                  amountInUnit
                  id
                }
                description {
                  language
                  text
                }
                id
                productId
                split
              }
              slug
              endTime {
                dateTime
                timeZone
              }
              startTime {
                dateTime
                timeZone
              }
              maxAttendees
              location
              id
              code
              category
              description {
                text
                language
              }
              overview {
                text
                language
              }
              objectives {
                text
                language
              }
              name {
                displayName
                language
              }
              eventId
              attendees {
                email
                organizer
                user {
                  profile {
                    firstname
                    lastname
                    bio
                    ids {
                      name
                      value
                    }
                    username
                    certifications {
                      category
                      issuedByName
                      countryOfOrigin
                    }
                    avatar {
                      contentUrl
                      medium
                    }
                  }
                  id
                  email
                  country
                  preferences {
                    knowsLanguages
                  }
                }
              }
              isVirtual
              recurringEventId
              lessons {
                participants {
                  userId
                }
              }
              pagination {
                pages
                items
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductEvents,
    }),

    productEventBySlug: builder.query<ProductEvent, { slug: string }>({
      query: variables => ({
        document: gql`
          query GetProductEventBySlug($slug: String!) {
            getProductEventBySlug(slug: $slug) {
              ${query}
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductEventBySlug,
    }),

    updateProductEventById: builder.mutation<
      { id: string },
      { id: string; productInput: Partial<ProductEvent> }
    >({
      query: variables => ({
        document: gql`
          mutation UpdateProductEventById($productInput: ProductEventInput!, $id: String) {
            updateProductEventById(productInput: $productInput, id: $id) {
              id
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.updateProductEventById,
    }),
  }),
});

export const {
  useProductEventsQuery,
  useLazyProductEventsQuery,
  useProductEventBySlugQuery,
  useUpdateProductEventByIdMutation,
} = productEvents;
